import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ListChecks } from 'lucide-react';
import { CLIENT_PAGE_GUIDES } from '../../../utils/clientPageGuides';

export function ClientPageHeader({ title, subtitle, actions = null, className = '' }) {
  return (
    <div
      className={`flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between ${className}`}
    >
      <div className="min-w-0">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-50 mb-1.5">
          {title}
        </h1>
        {subtitle ? (
          <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl leading-relaxed">
            {subtitle}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 shrink-0">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

export function ClientGuideCallout({
  title,
  children,
  steps = [],
  tone = 'slate',
  icon: Icon = ListChecks,
  action = null,
  className = '',
}) {
  const tones = {
    slate: {
      wrap: 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900',
      iconWrap:
        'bg-gray-50 border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200',
      title: 'text-gray-900 dark:text-gray-100',
      body: 'text-gray-600 dark:text-gray-400',
      step: 'text-gray-500 dark:text-gray-500',
    },
    sky: {
      wrap: 'border-sky-200/80 bg-sky-50/70 dark:border-sky-800/50 dark:bg-sky-950/30',
      iconWrap:
        'bg-white border-sky-100 text-sky-700 dark:bg-sky-950/50 dark:border-sky-800 dark:text-sky-300',
      title: 'text-sky-950 dark:text-sky-100',
      body: 'text-sky-900/80 dark:text-sky-200/80',
      step: 'text-sky-700 dark:text-sky-400',
    },
    amber: {
      wrap: 'border-amber-200/80 bg-amber-50/80 dark:border-amber-800/50 dark:bg-amber-950/30',
      iconWrap:
        'bg-white border-amber-100 text-amber-700 dark:bg-amber-950/50 dark:border-amber-800 dark:text-amber-300',
      title: 'text-amber-950 dark:text-amber-100',
      body: 'text-amber-900/80 dark:text-amber-200/80',
      step: 'text-amber-700 dark:text-amber-400',
    },
  };
  const t = tones[tone] || tones.slate;

  return (
    <div
      className={`rounded-2xl border px-4 py-3.5 sm:px-5 sm:py-4 shadow-sm ${t.wrap} ${className}`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border ${t.iconWrap}`}
        >
          <Icon className="w-3.5 h-3.5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          {title ? (
            <h2 className={`text-sm font-semibold tracking-tight ${t.title}`}>{title}</h2>
          ) : null}
          {children ? <div className={`mt-1 text-sm leading-relaxed ${t.body}`}>{children}</div> : null}
          {steps.length > 0 ? (
            <ol className={`mt-2.5 space-y-1 text-sm leading-snug ${t.title}`}>
              {steps.map((step, i) => (
                <li key={i} className="flex gap-2">
                  <span className={`font-semibold shrink-0 tabular-nums ${t.step}`}>{i + 1}.</span>
                  <span className={t.body}>{step}</span>
                </li>
              ))}
            </ol>
          ) : null}
          {action ? <div className="mt-3">{action}</div> : null}
        </div>
      </div>
    </div>
  );
}

export function ClientEmptyState({
  icon: Icon,
  title,
  description,
  actionLabel,
  actionTo,
  onAction,
  tips = [],
  className = '',
}) {
  return (
    <div
      className={`rounded-2xl border border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 p-8 sm:p-10 text-center ${className}`}
    >
      {Icon ? (
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400">
          <Icon className="w-7 h-7" aria-hidden />
        </div>
      ) : null}
      <p className="text-gray-900 dark:text-gray-50 font-semibold mb-1">{title}</p>
      {description ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-4">{description}</p>
      ) : null}
      {tips.length > 0 ? (
        <ul className="text-left max-w-sm mx-auto mb-5 space-y-1.5 text-sm text-gray-600 dark:text-gray-400">
          {tips.map((tip, i) => (
            <li key={i} className="flex gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {actionLabel && (actionTo || onAction) ? (
        actionTo ? (
          <Link
            to={actionTo}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium !rounded-xl bg-primary-600 text-white hover:bg-primary-700"
          >
            {actionLabel}
            <ArrowRight className="w-4 h-4" />
          </Link>
        ) : (
          <button
            type="button"
            onClick={onAction}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium !rounded-xl bg-primary-600 text-white hover:bg-primary-700"
          >
            {actionLabel}
            <ArrowRight className="w-4 h-4" />
          </button>
        )
      ) : null}
    </div>
  );
}
